import { useNavigate } from 'react-router-dom'
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import store from 'src/store'
import { CONFIG, IS_PROD, IS_SANDBOX } from 'src/utils/config'
import { centreAPIRequests, CentreApiRequests, getDevEnvConfigById } from 'src/api/centre'
import { getInitPersistedCentreEnv } from 'src/store/models/settings'
import { useEffect } from 'react'
import { KEYCLOAK } from 'src/utils/auth'

const REFRESH_TOKEN_SECONDS = 30 // proactively refresh when <=30s remain

///////////////////////////////////////////////////////////////////////////////
// TYPES
///////////////////////////////////////////////////////////////////////////////
export { STATUS } from './api-status'
export * from 'src/api/configurator'

type MyAxiosError = AxiosError

export type ApiDataBody<T> = { data: T }
export type ApiResponse<T> = [MyAxiosError, AxiosResponse<ApiDataBody<T>>]
export type ApiReturn<T> = Promise<ApiResponse<T>>

///////////////////////////////////////////////////////////////////////////////
// INSTANCE SETUP & HELPERS
///////////////////////////////////////////////////////////////////////////////

const selectedDevEnv = getDevEnvConfigById(getInitPersistedCentreEnv())

export const routerClient = axios.create({})
export const configuratorClient = axios.create({ baseURL: CONFIG.configurator_api_url })
const centreDevClient = axios.create({ baseURL: selectedDevEnv?.baseUrl ?? undefined })
const centreSandboxClient = axios.create({ baseURL: CONFIG.centre_prod_api_url })
const centreProdClient = axios.create({ baseURL: CONFIG.centre_prod_api_url })
export const brandingClient = axios.create({ baseURL: CONFIG.branding_url })
export const microservicesClient = axios.create({ baseURL: CONFIG.microservice_api_url })
export const reportingClient = axios.create({ baseURL: CONFIG.reporting_api_url })
export const bidlClient = axios.create({ baseURL: CONFIG.bidl_api_url })

const AUTHENTICATED_CLIENTS = [
  configuratorClient,
  brandingClient,
  microservicesClient,
  centreProdClient,
  centreSandboxClient,
  centreDevClient,
  routerClient,
  reportingClient,
  bidlClient,
]

// ---------------------------------------------------------------------------
// UNAUTHORIZED / 401 HANDLING (shared across all clients)
// ---------------------------------------------------------------------------
// We attach a response interceptor to every authenticated client. On the first 401 observed
// (after any refresh attempt by request interceptor) we clear auth state and emit a single
// 'app:unauthorized' event. The React provider listens and performs navigation exactly once.
// This avoids multiple redirects if several concurrent requests fail.

let unauthorizedRedirected = false
let unauthorizedInterceptorIds: number[] = []

function handleUnauthorizedResponse(error: AxiosError) {
  const status = error.response?.status
  if (status === 401) {
    if (!unauthorizedRedirected) {
      unauthorizedRedirected = true
      try {
        store.dispatch.auth.clearAuth()
      } catch {}
      // Fire a custom event; navigation handled in provider.
      globalThis.dispatchEvent(new CustomEvent('app:unauthorized'))
    }
  }
  throw error
}

function attachUnauthorizedResponseInterceptors(clients: AxiosInstance[]) {
  for (const client of clients) {
    const id = client.interceptors.response.use((response) => response, handleUnauthorizedResponse)
    unauthorizedInterceptorIds.push(id)
  }
}

function detachUnauthorizedResponseInterceptors(clients: AxiosInstance[]) {
  for (const client of clients) {
    const id = unauthorizedInterceptorIds.shift()
    if (typeof id === 'number') {
      client.interceptors.response.eject(id)
    }
  }
  unauthorizedRedirected = false
}

// External helper for models/services to explicitly clear 401 interceptors if needed.
export const detachAllUnauthorizedInterceptors = () => {
  detachUnauthorizedResponseInterceptors(AUTHENTICATED_CLIENTS)
}

export const AxiosInterceptorProvider = ({ children }) => {
  const navigate = useNavigate()

  useEffect(() => {
    // Attach global unauthorized interceptors once on mount.
    attachUnauthorizedResponseInterceptors(AUTHENTICATED_CLIENTS)

    const handleUnauthorized = () => {
      // Perform navigation once per unauthorized event.
      navigate('/no-auth')
    }
    globalThis.addEventListener('app:unauthorized', handleUnauthorized)

    return () => {
      globalThis.removeEventListener('app:unauthorized', handleUnauthorized)
      detachUnauthorizedResponseInterceptors(AUTHENTICATED_CLIENTS)
    }
  }, [navigate])

  return children
}

export const setAxiosHeader = (key: string, value: string) => {
  configuratorClient.defaults.headers.common[key] = value
}

export const removeAxiosHeader = (key: string) =>
  delete configuratorClient.defaults.headers.common[key]

// Set Authorization header on all authenticated axios instances.
// Centralized via AUTHENTICATED_CLIENTS to avoid manual updates when clients change.
export const setJwtAuthHeaders = (accessToken: string) => {
  const authHeader = `Bearer ${accessToken}`
  for (const client of AUTHENTICATED_CLIENTS) {
    client.defaults.headers['Authorization'] = authHeader
  }
}

// Request-time guard (refresh & header application)
attachAccessTokenGuard(AUTHENTICATED_CLIENTS)
// Response-time unauthorized handling
// (This will also run in the provider to ensure event listener exists; double attach avoided by provider cleanup.)
// NOTE: We intentionally DO NOT call attachUnauthorizedResponseInterceptors here to allow provider control.

///////////////////////////////////////////////////////////////////////////////

export function updateCentreDevClientBaseUrl(baseUrl: string) {
  centreDevClient.defaults.baseURL = baseUrl
}

///////////////////////////////////////////////////////////////////////////////

interface CentreNamedApis {
  prod: CentreApiRequests
  uat: CentreApiRequests
}

/**
 * All possible ways to make a Centre API request.
 *
 * "Current" environment (77 usages):  `centreAPIs.getUsers()`
 * "Other" environment (9 usages):     `centreAPIs.otherEnvironment.getUsers()`
 * Static access to prod (1 use):      `centreAPIs.prod.getUsers()`
 * Static access to uat (1 use):       `centreAPIs.uat.getUsers()`
 */
export const centreAPIs = (() => {
  if (IS_PROD) {
    return setupCentreClient(centreProdClient)
  }
  if (IS_SANDBOX) {
    return setupCentreClient(centreSandboxClient)
  }

  return setupCentreClient(centreDevClient)
})()

function setupCentreClient(client) {
  const aliasesToCurrentAPI = Object.keys(centreAPIRequests).reduce((requests, requestName) => {
    return {
      ...requests,
      [requestName]: centreAPIRequests[requestName](client),
    }
  }, {} as CentreApiRequests)

  const namedAPIs: CentreNamedApis = {
    prod: aliasesToCurrentAPI,
    uat: aliasesToCurrentAPI,
  }

  // Use named APIs based on context of currently selected environment
  const contextAwareAPIs = {
    get currentEnvironment() {
      return aliasesToCurrentAPI
    },
    get otherEnvironment() {
      // temporary - removing otherEnvironment() in progress
      if (true) {
        throw new Error('The `otherEnvironment` property is no longer supported')
      }
      return aliasesToCurrentAPI
    },
  }

  return Object.assign(contextAwareAPIs, namedAPIs, aliasesToCurrentAPI)
}

///////////////////////////////////////////////////////////////////////////////

// Single-flight refresh: concurrent requests share one refresh promise to avoid
// multiple network calls and race conditions updating headers / tokens.
let inflightRefreshPromise: Promise<boolean> | null = null

function attachAccessTokenGuard(clients: AxiosInstance[]) {
  for (const client of clients) {
    client.interceptors.request.use(async (config) => {
      const hasActiveToken = await ensureActiveToken()

      if (!hasActiveToken) {
        throw new Error('Access token expired')
      }

      // Always apply the freshest token in case a refresh just occurred.
      if (KEYCLOAK.token) {
        // Axios 1.x uses AxiosHeaders; avoid assigning a plain object.
        if (config.headers?.set) {
          // AxiosHeaders instance
          config.headers.set('Authorization', `Bearer ${KEYCLOAK.token}`)
        } else {
          // Fallback: create AxiosHeaders if not present
          const headers = new axios.AxiosHeaders(config.headers)
          headers.set('Authorization', `Bearer ${KEYCLOAK.token}`)
          config.headers = headers
        }
      }

      return config
    })
  }
}

async function refreshToken() {
  try {
    const refreshed = await KEYCLOAK.updateToken(30)
    if (refreshed) {
      sessionStorage.setItem('kc-access-token', KEYCLOAK.token)
      setJwtAuthHeaders(KEYCLOAK.token)
      return true
    }
    return false
  } catch {
    return false
  } finally {
    inflightRefreshPromise = null
  }
}

async function ensureActiveToken() {
  const tokenExpired = isTokenExpired()
  if (!tokenExpired) {
    return true
  }

  if (!KEYCLOAK.refreshToken) {
    return false
  }

  inflightRefreshPromise ??= refreshToken()

  return inflightRefreshPromise
}

function isTokenExpired() {
  if (!KEYCLOAK?.tokenParsed?.exp) {
    return false
  }

  const nowInSeconds = Date.now() / 1000
  const skew = KEYCLOAK.timeSkew ?? 0
  const expiresAt = KEYCLOAK.tokenParsed.exp + skew
  const secondsRemaining = expiresAt - nowInSeconds

  return secondsRemaining <= REFRESH_TOKEN_SECONDS
}
